import { memo } from 'react';
import { useRouter } from 'next/router';
import { request } from 'graphql-request';

import PageWrapper from '~/components/wrappers/pageWrapper';
import VideoPage from '~/components/videoPage/videoPage';
import { getAlias, getContentPaths, getDrupalSettings } from '~/utils/utils';
import { findVideoInTrays } from '~/utils/youtube';
import { getLatestYouTubeVideos, getYouTubePlaylistsFromIdArray, getYouTubeVideoData } from '~/utils/youtube.server';
import { getYoutubePlaylists } from '~/utils/queries';
import GET_VIDEO from '~/graphql/GET_VIDEO.graphql';

/**
 * Video page – displays a single video with playlist context.
 */
const Video = memo((props) => {
  const router = useRouter();

  if (router.isFallback) {
    return <span />;
  }

  return <VideoPage {...props} />;
});

export async function getStaticPaths() {
  const paths = await getContentPaths('/videos/' ,{
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'video'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const data = await getYoutubePlaylists();
  const aliasLastSegment = ctx?.params?.alias?.[ctx?.params?.alias?.length - 1];
  const isYouTubeVideoId = aliasLastSegment && /^[a-zA-Z0-9_-]{11}$/.test(aliasLastSegment);

  let trays = [];

  try {
    const [latest, playlists] = await Promise.all([
      getLatestYouTubeVideos(),
      getYouTubePlaylistsFromIdArray(data?.entitySubQueueQuery?.items ?? [], 'array'),
    ]);
    trays = [
      { id: 'latest', category: 'Latest', items: latest || [] },
      ...playlists.map((playlist) => ({
        id: playlist.id,
        category: playlist.category,
        items: playlist.items
      }))
    ];
  } catch (e) {
    console.error('Failed to fetch YouTube playlists:', e);
  }

  let { video: foundVideo, trayIndex, playlistIndex } = findVideoInTrays(trays, aliasLastSegment);
  const drupalSettings = await getDrupalSettings();

  if (isYouTubeVideoId && !foundVideo) {
    const videoQuery = await getYouTubeVideoData(aliasLastSegment);

    if (videoQuery && videoQuery.channel_id === process.env.NEXT_PUBLIC_YOUTUBE_CHANNEL_ID) {
      foundVideo = videoQuery;
      trays[0].items.unshift(foundVideo);
  
      trayIndex = 0;
      playlistIndex = 0;
    }
  }

  let oldMpxVideo = null;
  if (!isYouTubeVideoId) {
    // Get Alias
    const aliasData = await getAlias({
      alias: `/videos/${ ctx.params.alias.join('/') }`
    });

    if (aliasData?.pathAliasQuery?.nid) {
      // Get Data
      oldMpxVideo = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_VIDEO, {
        target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
      });
    }
  }

  if (!foundVideo && !oldMpxVideo) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  return {
    props: {
      content: foundVideo,
      drupalSettings,
      oldMpxVideo,
      playlistIndex,
      trayIndex,
      youTubeVideoId: isYouTubeVideoId ? aliasLastSegment : null,
      youtubePlaylists: trays
    },
    revalidate: 1
  };
}

export default PageWrapper(Video);